import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dynamicFlowsService,
  type FlowDetail,
  type FlowListItem,
  type FlowVideo,
} from '@/services/dynamicFlowsService';

export const dynamicFlowKeys = {
  all: ['dynamicFlows'] as const,
  list: () => [...dynamicFlowKeys.all, 'list'] as const,
  detail: (id: string) => [...dynamicFlowKeys.all, 'detail', id] as const,
  videos: (id: string) => [...dynamicFlowKeys.all, 'videos', id] as const,
  metrics: (id: string, from: string, to: string) =>
    [...dynamicFlowKeys.all, 'metrics', id, from, to] as const,
};

export function useFlowsList() {
  return useQuery({ queryKey: dynamicFlowKeys.list(), queryFn: dynamicFlowsService.list });
}

// O editor guarda o rascunho no próprio store; esta query só faz a carga inicial.
export function useFlow(id: string | undefined) {
  return useQuery({
    queryKey: dynamicFlowKeys.detail(id || ''),
    queryFn: () => dynamicFlowsService.get(id as string),
    enabled: !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// Enquanto houver vídeo processando, consulta de novo a cada 5 s.
export function useFlowVideos(id: string | undefined) {
  return useQuery({
    queryKey: dynamicFlowKeys.videos(id || ''),
    queryFn: () => dynamicFlowsService.listVideos(id as string),
    enabled: !!id,
    refetchInterval: (query) => {
      const videos = query.state.data as FlowVideo[] | undefined;
      const pending = videos?.some(
        (video) => !video.ready && !String(video.processing_status).startsWith('failed'),
      );
      return pending ? 5000 : false;
    },
  });
}

export function useFlowMetrics(id: string | undefined, range: { from: string; to: string }) {
  return useQuery({
    queryKey: dynamicFlowKeys.metrics(id || '', range.from, range.to),
    queryFn: () => dynamicFlowsService.metrics(id as string, range),
    enabled: !!id && !!range.from && !!range.to,
  });
}

export function useCreateFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => dynamicFlowsService.create(name),
    onSuccess: (flow: FlowDetail) => {
      queryClient.setQueryData(dynamicFlowKeys.detail(flow.id), flow);
      queryClient.invalidateQueries({ queryKey: dynamicFlowKeys.list() });
    },
  });
}

const patchListItem = (
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  patch: Partial<FlowListItem>,
) => {
  queryClient.setQueryData<FlowListItem[]>(dynamicFlowKeys.list(), (items) =>
    items?.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  );
};

export function useRenameFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => dynamicFlowsService.rename(id, name),
    onSuccess: (result) => patchListItem(queryClient, result.id, { name: result.name }),
  });
}

export function usePublishFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dynamicFlowsService.publish(id),
    onSuccess: (result, id) => {
      patchListItem(queryClient, id, {
        status: result.status,
        published_version: result.version,
        has_unpublished_changes: false,
      });
      queryClient.invalidateQueries({ queryKey: dynamicFlowKeys.list() });
    },
  });
}

export function usePauseFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dynamicFlowsService.pause(id),
    onSuccess: (result, id) => patchListItem(queryClient, id, { status: result.status }),
  });
}

export function useResumeFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dynamicFlowsService.resume(id),
    onSuccess: (result, id) => patchListItem(queryClient, id, { status: result.status }),
  });
}

export function useDeleteFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dynamicFlowsService.remove(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<FlowListItem[]>(dynamicFlowKeys.list(), (items) =>
        items?.filter((item) => item.id !== id),
      );
      queryClient.removeQueries({ queryKey: dynamicFlowKeys.detail(id) });
    },
  });
}

export function useUploadFlowVideo(flowId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (percent: number) => void }) =>
      dynamicFlowsService.uploadVideo(flowId, file, onProgress),
    onSettled: () => queryClient.invalidateQueries({ queryKey: dynamicFlowKeys.videos(flowId) }),
  });
}

export function useRenameFlowVideo(flowId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ videoId, name }: { videoId: string; name: string }) =>
      dynamicFlowsService.renameVideo(flowId, videoId, name),
    onSuccess: (_, { videoId, name }) =>
      queryClient.setQueryData<FlowVideo[]>(dynamicFlowKeys.videos(flowId), (videos) =>
        videos?.map((video) => (video.id === videoId ? { ...video, name } : video)),
      ),
  });
}

export function useDeleteFlowVideo(flowId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (videoId: string) => dynamicFlowsService.deleteVideo(flowId, videoId),
    onSuccess: (_, videoId) =>
      queryClient.setQueryData<FlowVideo[]>(dynamicFlowKeys.videos(flowId), (videos) =>
        videos?.filter((video) => video.id !== videoId),
      ),
  });
}
